<template>
  <div class="home">
    <!-- HEADER -->
    <header class="header">
      <h1>Apartments Gavranović</h1>
      <div class="language-menu">
        <button class="hamburger" @click="toggleMenu">☰</button>
        <ul v-if="menuOpen" class="dropdown">
          <li v-for="lang in languages" :key="lang.code" @click="setLanguage(lang.code)">
            {{ lang.name }}
          </li>
        </ul>
      </div>
    </header>

    <!-- HERO BANNER -->
    <section class="hero-banner">
      <img :src="images[16]" alt="Apartment banner" />
    </section>

    <!-- GALLERY -->
    <section class="gallery">
      <button class="arrow left" @click="prevImage" aria-label="Previous image">‹</button>
      <div class="carousel">
        <img
          v-if="images[prevIndex]"
          :src="images[prevIndex]"
          alt="Previous image"
          class="side-image left"
          @click="prevImage"
          tabindex="0"
          @keyup.enter="prevImage"
        />
        <img
          :src="images[currentIndex]"
          alt="Apartment image"
          class="main-image"
          @click="openLightbox = true"
        />
        <img
          v-if="images[nextIndex]"
          :src="images[nextIndex]"
          alt="Next image"
          class="side-image right"
          @click="nextImage"
          tabindex="0"
          @keyup.enter="nextImage"
        />
      </div>
      <button class="arrow right" @click="nextImage" aria-label="Next image">›</button>
    </section>

    <!-- AMENITIES -->
    <section class="amenities-icons reveal">
      <div class="icon-item">
        <img :src="iconBath" alt="Bathroom icon" class="icon-img" />
        <p>1x {{ t('Bathroom') || 'Bathroom' }}</p>
      </div>
      <div class="icon-item">
        <img :src="iconBed" alt="Bed icon" class="icon-img" />
        <p>1x {{ t('Bedroom') || 'Bedroom' }}</p>
      </div>
      <div class="icon-item">
        <img :src="iconPerson" alt="Person icon" class="icon-img" />
        <p>2+1 {{ t('Guests') || 'Guests' }}</p>
      </div>
      <div class="icon-item">
        <img :src="iconWifi" alt="WiFi icon" class="icon-img" />
        <p>{{ t('WiFi') || 'WiFi' }}</p>
      </div>
      <div class="icon-item">
        <img :src="iconParking" alt="Parking icon" class="icon-img" />
        <p>{{ t('Parking') || 'Parking' }}</p>
      </div>
      <div class="icon-item">
        <img :src="iconGrill" alt="Grill icon" class="icon-img" />
        <p>{{ t('Grill') || 'Grill' }}</p>
      </div>
    </section>

    <!-- MAIN CONTENT -->
    <main class="main-content">
      <div class="info-box reveal">
        <p>{{ t('description') }}</p>
      </div>

      <div class="section-divider"></div>

      <!-- MAP -->
      <div class="map-container reveal">
        <h2>{{ t('location') }}</h2>
        <l-map
          v-model:zoom="zoom"
          v-model:center="center"
          :use-global-leaflet="false"
          :min-zoom="10"
          :max-zoom="17"
        >
          <l-tile-layer
            :url="tileProviders[0].url"
            :attribution="tileProviders[0].attribution"
          />
          <l-marker :lat-lng="markerLatLng">
            <l-popup>Apartmani Gavranović</l-popup>
          </l-marker>
        </l-map>
      </div>

      <div class="section-divider"></div>

      <!-- Nearby & Distances -->
      <section class="nearby-section reveal">
        <h2>{{ t('Nearby') || 'Nearby & Distances' }}</h2>
        <ul class="nearby-list">
          <li><strong>{{ t('sea') || 'Sea' }}:</strong> 150 m</li>
          <li><strong>{{ t('beach') || 'Beach'}}:</strong> 150 m</li>
          <li><strong>{{ t('bar')|| 'Bar'}}:</strong> 130 m</li>
          <li><strong>{{ t('shop') || 'Closest shop' }}:</strong> 500 m</li>
          <li><strong>{{ t('restaurant') || 'Restaurants' }}:</strong> 7 min walk</li>
          <li><strong>{{ t('city') || 'City center' }}:</strong> 10 min drive</li>
          <li><strong>{{ t('pharmacy')|| 'Pharmacy'}}:</strong> 10 min drive</li>
        </ul>
      </section>

      <div class="section-divider"></div>

      <!-- PRICING -->
      <section class="pricing-section reveal">
        <h2>{{ t('Pricing') || 'Seasonal Pricing' }}</h2>
        <table class="pricing-table">
          <thead>
            <tr>
              <th>{{ t('season') || 'Season' }}</th>
              <th>{{ t('period') || 'Period' }}</th>
              <th>{{ t('price') || 'Price / Night' }}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{{ t('preseason') || 'Pre-season' }}</td>
              <td>01.05.-31.05.</td>
              <td>€70</td>
            </tr>
            <tr>
              <td>{{ t('preseason') || 'Pre-season' }}</td>
              <td>01.06.-30.06.</td>
              <td>€75</td>
            </tr>
            <tr>
              <td>{{ t('peakseason') || 'Peak season'  }}</td>
              <td>01.07.-10.07.</td>
              <td>€85</td>
            </tr>
            <tr>
              <td>{{ t('peakseason') || 'Peak season' }}</td>
              <td>11.07.-14.08.</td>
              <td>€100</td>
            </tr>
            <tr>
              <td>{{ t('peakseason') || 'Peak season' }}</td>
              <td>15.08.-22.08.</td>
              <td>€90</td>
            </tr>
            <tr>
              <td>{{ t('postseason') || 'Postseason' }}</td>
              <td>23.08.-31.08.</td>
              <td>€80</td>
            </tr>
            <tr>
              <td>{{t('postseason') || 'Postseason' }}</td>
              <td>01.09.-01.10.</td>
              <td>€70</td>
            </tr>
          </tbody>
        </table>
      </section>
    </main>

    <!-- FOOTER -->
    <footer class="footer">
      <div class="footer-left">
        <p>&copy; 2026 Apartments Gavranović</p>
      </div>
      <div class="footer-center">
        <p>📍 Krčkih iseljenika 18, Malinska, Croatia | ✉️ malukic8@gmail.com | 📞 (+385)91 735 2856</p>
      </div>
      <div class="footer-right">
        <p>Created by: <a href="https://yourwebsite.com" target="_blank" rel="noopener">Loris Lukić</a></p>
      </div>
    </footer>

    <!-- LIGHTBOX -->
    <div v-if="openLightbox" class="lightbox" @click.self="openLightbox = false">
      <img :src="images[currentIndex]" alt="Apartment large image" />
      <button class="close-btn" @click="openLightbox = false" aria-label="Close popup">✕</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted} from 'vue'
import { useI18n } from 'vue-i18n'

// === Leaflet uvozi ===
import { LMap, LTileLayer, LMarker, LPopup } from '@vue-leaflet/vue-leaflet'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'

// ✅ Popravak ikona markera (100% radi u Vite/Webpack okruženju)
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow
})
// === KRAJ POPRAVKA IKONE MARKERA ===

// === Leaflet Map podaci ===
const APARTMENT_LATITUDE = 45.116266
const APARTMENT_LONGITUDE = 14.515836

const zoom = ref(15)
const center = ref([APARTMENT_LATITUDE, APARTMENT_LONGITUDE])
const markerLatLng = ref([APARTMENT_LATITUDE, APARTMENT_LONGITUDE])

const tileProviders = [
  {
    name: 'OpenStreetMap',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution:
      '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors'
  }
]

// === GALERIJA ===
import img1 from './assets/Gallery/Gav.2.jpg'
import img2 from './assets/Gallery/Gav.1.jpeg'
import img3 from './assets/Gallery/Gav.3.jpg'
import img4 from './assets/Gallery/Gav.4.jpg'
import img5 from './assets/Gallery/Gav.5.jpeg'
import img6 from './assets/Gallery/Gav.6.jpeg'
import img7 from './assets/Gallery/Gav.7.jpeg'
import img8 from './assets/Gallery/Gav.8.jpeg'
import img9 from './assets/Gallery/Gav.9.jpeg'
import img10 from './assets/Gallery/Gav.10.jpeg'
import img11 from './assets/Gallery/Gav.11.jpeg'
import img12 from './assets/Gallery/Gav.12.jpeg'
import img13 from './assets/Gallery/Gav.13.jpeg'
import img14 from './assets/Gallery/Gav.14.jpeg'
import img15 from './assets/Gallery/Gav.15.jpeg'
import img16 from './assets/Gallery/Gav.16.jpeg'
import img17 from './assets/Gallery/Gav.banner.jpg'

const images = [img1, img2, img3, img4, img5, img6, img7, img8, img9, img10, img11, img12, img13, img14, img15, img16, img17]
const currentIndex = ref(0)
const openLightbox = ref(false)

const prevIndex = computed(() => (currentIndex.value - 1 + images.length) % images.length)
const nextIndex = computed(() => (currentIndex.value + 1) % images.length)

function prevImage() {
  currentIndex.value = prevIndex.value
}

function nextImage() {
  currentIndex.value = nextIndex.value
}

// === NEW: Uvoz ikona iz src/assets/Icons using the reliable @/ alias ===
// NOTE: If you still get errors here, you may need to change '@/assets/' to '../assets/' or './assets/'
import iconBath from '@/assets/Icons/Bath_icon.png'
import iconBed from '@/assets/Icons/Bed_icon.png'
import iconGrill from '@/assets/Icons/Grill_icon.png'
import iconParking from '@/assets/Icons/Parking_icon.png'
import iconPerson from '@/assets/Icons/Person_icon.png'
import iconWifi from '@/assets/Icons/Wifi_icon.png'
// === END NEW ICON IMPORTS ===

// === JEZICI ===
const { t, locale } = useI18n()

const menuOpen = ref(false)
function toggleMenu() {
  menuOpen.value = !menuOpen.value
}

function setLanguage(lang) {
  locale.value = lang
  menuOpen.value = false
}

const languages = [
  { code: 'hr', name: 'Hrvatski' },
  { code: 'en', name: 'English' },
  { code: 'sl', name: 'Slovenščina' },
  { code: 'it', name: 'Italiano' },
  { code: 'de', name: 'Deutsch' }
]

onMounted(() => {
  const observerOptions = {
    threshold: 0.15 
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('active')
      } else {
        // Remove this if you want the animation to happen ONLY once
        entry.target.classList.remove('active')
      }
    })
  }, observerOptions)

  // This looks for every element you gave the "reveal" class to
  const revealElements = document.querySelectorAll('.reveal')
  revealElements.forEach((el) => observer.observe(el))
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;700&display=swap');

.home {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}




/* Header sits on top of the banner */
/* Header sits on top of the banner */
.header {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  z-index: 20;
  color: #fff;
  box-sizing: border-box;
}

@media (max-width: 600px) {
  .header {
    padding: 0.75rem 1rem;
  }

  .header h1 {
    font-size: 1.4rem;
  }
}

.header h1 {
  font-family: 'Dancing Script', cursive;
  margin: 0;
  font-size: 1.8rem;
  color: #fff;
}

.header::before {
  content: '';
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: -1;
}




/* Ensure language-menu container doesn’t get pushed */
.language-menu { position: relative; display: flex; align-items: center; }
.header .hamburger { z-index: 30; color: #fff; background: none; border: none; font-size: 1.8rem; cursor: pointer; }
.dropdown { position: absolute; right: 0; top: 2.5rem; background: white; border: 1px solid #ccc; border-radius: 8px; list-style: none; padding: 0.5rem 0; margin: 0; z-index: 1000; min-width: 120px; box-shadow: 0 4px 8px rgba(0,0,0,0.2); }
.dropdown li { padding: 0.5rem 1rem; cursor: pointer; color: black; text-align: right; }
.dropdown li:hover { background-color: #f0f0f0; }

/* Banner overlay */
.hero-banner { position: relative; width: 100%; height: 40vh; overflow: hidden; flex-shrink: 0; }
.hero-banner img { width: 100%; height: 100%; object-fit: cover; }
.hero-banner::after { content: ''; position: absolute; inset: 0; background: rgba(0,0,0,0.2); z-index: 1; }

/* MAIN CONTENT */
.main-content { flex: 1; display: flex; flex-direction: column; justify-content: flex-start; align-items: center; padding: 2rem; gap: 2rem; width: 100%; }





.gallery {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 0;
  padding-top: 5rem;
}

.carousel {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 900px;
  height: 420px; /* ADD THIS */
  aspect-ratio: 16 / 9;
  height: auto;
  overflow: hidden;
  user-select: none;
  gap: 0;
}

@media (max-width: 768px) {
  .side-image {
    display: none;
  }

  .main-image {
    width: 100%;
  }
}

.carousel img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}



.main-image {
  width: 60%;
  height: 100%;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.3);
  cursor: pointer;
  flex-shrink: 0;
  transition: transform 0.3s ease;
  position: relative;
  z-index: 10;
}

.side-image {
  width: 20%;
  height: 80%;
  border-radius: 8px;
  object-fit: cover;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.3s ease, transform 0.3s ease;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  filter: brightness(0.85);
}

.side-image.left {
  margin-right: 20px;
}

.side-image.right {
  margin-left: 20px;
}

.side-image:hover {
  opacity: 1;
  transform: scale(1.05);
  filter: brightness(1);
}

.arrow {
  font-size: 2rem;
  background: none;
  border: none;
  cursor: pointer;
  color: #2c3e50;
  user-select: none;
  padding: 0 0.5rem;
  transition: color 0.3s;
  z-index: 2;
}

.arrow:hover {
  color: #3498db;
}

/* AŽURIRANI STILOVI za fleksibilni raspored */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start; 
  align-items: center;
  padding: 2rem;
  gap: 2rem;
}

.info-box {
  max-width: 900px;
  padding: 1rem 0;
  font-size: 1.36rem;
  line-height: 1.6;
  text-align: center;
}

@media (max-width: 600px) {
  body {
    font-size: 15px;
  }

  .info-box,
  .nearby-list li,
  .pricing-table {
    font-size: 1rem;
  }
}


/* NEW: Styles for the Amenities Icons */
.amenities-icons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3rem; /* Spacing between icons */
  padding: 1rem 0 2rem; /* Add some vertical space */
  max-width: 900px;
  margin: 0 auto;
  flex-wrap: wrap; /* Allows icons to wrap on smaller screens */
}

.icon-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: #2c3e50; /* Dark text color */
  font-size: 0.9rem;
  font-weight: 500;
  width: 80px; /* fixed width for better alignment */
}

.icon-img {
  width: 40px; /* Size of the icon image */
  height: 40px;
  margin-bottom: 0.4rem;
}
/* END NEW ICON STYLES */

/* AŽURIRANI STILOVI za fleksibilni raspored */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start; 
  align-items: center;
  padding: 0 1rem 2rem; /* Adjusted top padding since icons section is new */
  gap: 2rem;
}

@media (max-width: 600px) {
  .main-content {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}


/* STILOVI ZA KARTU */
.map-container {
  width: 100%;
  max-width: 1000px; /* manja širina karte */
  height: 400px; /* manja visina karte */
  margin: 0 auto;
  border: 2px solid #2c3e50;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(0,0,0,0.15);
  background: white;
  z-index: 50; /* da bude iznad ostalog sadržaja */
}

@media (max-width: 768px) {
  .map-container {
    height: 250px;
  }
}

.map-container h2 {
  text-align: center;
  font-size: 1.1rem;
  color: #2c3e50;
  margin: 0.5rem 0;
}

/* Leaflet karta popunjava cijeli blok ispod naslova */
.leaflet-container {
  width: 100%;
  height: calc(100% - 1.8rem);
}

.footer {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: 1.5rem 2rem;
  background-color: #2c3e50;
  color: white;
  font-size: 1rem;
  text-align: center;
  width: 100%;
  box-sizing: border-box;
}


.footer-left,
.footer-center,
.footer-right {
  margin: 0.5rem 0;
}

@media (max-width: 600px) {
  .footer-center p {
    white-space: normal;
  }

  .footer {
    flex-direction: column;
  }
}

.footer-right a {
  color: #fff;
  text-decoration: underline;
}

.footer-right a:hover {
  color: #3498db;
}


.lightbox {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0,0,0,0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.lightbox img {
  max-width: 90vw;
  max-height: 90vh;
  border-radius: 8px;
  box-shadow: 0 0 30px white;
}

.close-btn {
  position: fixed;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 2rem;
  color: white;
  cursor: pointer;
  user-select: none;
}

/* NEARBY SECTION */
.nearby-section {
  max-width: 900px;
  margin: 3rem auto 2rem;
  padding: 1.5rem;
  text-align: center;
}

.nearby-section h2 {
  margin-bottom: 1rem;
  color: #2c3e50;
}

.nearby-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.nearby-list li {
  margin: 0.5rem 0;
  font-size: 1.26rem;
}

/* PRICING SECTION */
.pricing-section {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto 3rem;
  padding-left: 1rem;
  padding-right: 1rem;
  text-align: center;
}

.pricing-section h2 {
  margin-bottom: 1rem;
  color: #2c3e50;
}

.pricing-table {
  width: 100%;
}

@media (max-width: 600px) {
  .pricing-table {
    display: block;
    overflow-x: auto;
  }
}

.pricing-table th,
.pricing-table td {
  border: 1px solid #ddd;
  padding: 1rem 1.5rem;
  text-align: center
}

.pricing-table th {
  background-color: #f0f0f0;
  font-weight: 600;
}

.pricing-table tbody tr:nth-child(even) {
  background-color: #fafafa; /* optional stripe for readability */
}

.section-divider {
  width: 100%;
  max-width: 900px;
  height: 2px;
  background-color: #000;
  margin: 3rem auto;
}

/* Make section headings bigger */
.nearby-section h2,
.pricing-section h2,
.map-container h2 {
  font-size: 1.6rem; /* bigger headings */
  margin-bottom: 1rem;
}

/* 1. The starting state (Hidden) */
.reveal {
  opacity: 0;
  transform: translateY(40px);
  transition: all 0.8s ease-out;
  visibility: hidden;
}

/* 2. The visible state (Active) */
.reveal.active {
  opacity: 1;
  transform: translateY(0);
  visibility: visible;
}

/* Optional: Subtle delay for the divider lines */
.section-divider.reveal {
  transform: scaleX(0); /* Divider grows from center */
  transition: transform 1s ease;
}

.section-divider.reveal.active {
  transform: scaleX(1);
}

</style>

<style>
html, body {
  margin: 0 !important;
  padding: 0 !important;
  width: 100%;
  overflow-x: hidden; 
}
*,
*::before,
*::after {
  box-sizing: border-box;
}
</style>